import React from 'react';
import styled from 'styled-components';
import { Layout } from '@components/Layout';
import { Bold } from '@components/Text';
import { colorPalette } from '@lib/nvstr-utils.es';
import { PATHS } from '@constants/paths';
import { TrackingEvents } from '../tracking';
import { parseQueryString } from 'utils/usefulFuncs';
import { usePublicPageSessionMessage } from '@utils/hooks/usePublicPageSessionMessage';
import { IS_WHITE_LABELED } from '../appConfig';
import { Container } from '@lib/nvstr-common-ui.es';
import AltSplash from '../pages/_altSplash';
import { FullLogo } from '@components/Images/LogoFull';
import { SkeletonButton } from '@components/Buttons/SkeletonButton';
import { useWindowSize } from '@hooks/useWindowSize';
import { SizeableTornadoLines, TORNADO_LINES_ASPECT_RATIO } from '@components/Images/SizeableTornadoLines';

const Wrapper = styled.div`
  background: #222;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;
const TornadoOutlinesWrapper = styled.div`
  position: fixed;
  left: ${({ Left }) => Left || 0}px;
  z-index: 2;
`;
const GradientWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  height: 100vh;
  width: 100vw;

  background: #222;
  opacity: 0.84;
  overflow: hidden;
`;
const LeftGradientWrapper = styled.div`
  position: fixed;
  left: -55%;
  bottom: -55%;
  z-index: 1;
`;
const RightGradientWrapper = styled.div`
  position: fixed;
  right: -65%;
  top: -55%;
  z-index: 1;
`;
const FrameWrapper = styled.div`
  z-index: 3;

  margin: 0 auto;
  max-width: 1400px;

  padding: 48px 108px 108px 108px;

  h1 {
    font-size: 108px;
    line-height: 1.4;
    text-transform: uppercase;
  }

  * {
    color: #fff !important;
  }

  @media (max-height: 860px) {
    h1 {
      max-width: 500px;
      font-size: 78px;
    }
  }
  @media (max-height: 740px) {
    h1 {
      max-width: 440px;
      font-size: 64px;
    }
  }
  @media (max-height: 660px) {
    h1 {
      max-width: 330px;
      font-size: 48px;
    }
  }

  @media (max-width: 1200px) {
    padding-left: 48px;
    padding-right: 48px;
  }

  @media (max-width: 840px) {
    h1 {
      max-width: 430px;
    }
  }

  @media (max-width: 800px) {
    padding-left: 24px;
    padding-right: 24px;
  }

  @media (max-width: 525px) {
    h1 {
      max-width: 300px;
      font-size: 36px;
    }
  }
`;
const LogoWrapper = styled.div`
  position: relative;
  z-index: 3;
`;
const JumboMessage = styled.div`
  position: relative;
  z-index: 2;

  padding: 108px 0 0 0;

  @media (max-width: 840px) {
    padding: 48px 0 0 0;
  }
`;
const ActionsWrapper = styled.div`
  position: fixed;
  z-index: 2;
  left: 108px;
  bottom: 48px;

  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: calc(100vw - 216px);
  gap: 24px;

  padding: 108px 0 0 0;

  button {
    padding: 24px 48px;
    width: 100%;
    max-width: 480px;

    border-color: #fff !important;

    * {
      color: #fff !important;
    }

    &:hover {
      background: #fff !important;
      border-color: #fff !important;
      * {
        color: #222 !important;
      }
    }
  }

  @media (max-height: 1100px) {
    button {
      padding: 24px 24px;
    }
  }

  @media (max-width: 1200px) {
    width: calc(100vw - 96px);
    left: 48px;
  }

  @media (max-width: 800px) {
    width: calc(100vw - 48px);
    left: 24px;
  }

  @media (max-width: 840px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    left: 0;
    padding: 0 16px;

    & div {
      width: 100%;
      flex: 1;
      text-align: center;
    }
    button {
      padding-top: 16px;
      padding-bottom: 16px;
      width: 100%;
    }
  }
`;

function CircularGradient({ size = 100, color = '#00FFFF' }) {
  const gradientStyle = {
    width: size,
    height: size,
    borderRadius: '50%',

    background: `radial-gradient(50% 44.96% at 50% 55.04%, ${color} 30.21%, #222 100%)`,

    opacity: 0.4,
    pointerEvents: 'none',
  };

  return <div style={gradientStyle} />;
}

function calcLeft(windowHeight, windowWidth) {
  const width = TORNADO_LINES_ASPECT_RATIO * windowHeight;
  return (windowWidth - width) / 2;
}

const Home = () => {
  const { SessionMessageComponent, setSessionMessage } = usePublicPageSessionMessage();
  const size = useWindowSize();

  React.useEffect(() => {
    const properties = {};
    TrackingEvents.splash.viewHomepage.send(properties);
  }, []);

  React.useEffect(() => {
    const handleAuthParams = (query) => {
      if (query.loginRequired || query.sessionExpired) {
        const message = query.loginRequired ? (
          <>
            Please{' '}
            <a href={PATHS.SIGN_IN} style={{ textDecoration: 'none' }}>
              <Bold>log in</Bold>
            </a>{' '}
            to continue
          </>
        ) : (
          <>
            You were logged out due to inactivity. Please{' '}
            <a href={PATHS.SIGN_IN} style={{ textDecoration: 'none' }}>
              <Bold>log in</Bold>
            </a>{' '}
            to continue.
          </>
        );
        setSessionMessage(message);
      }
    };
    const query = parseQueryString(window.location.search);
    handleAuthParams(query);
  }, []);

  if (IS_WHITE_LABELED) {
    return <AltSplash SessionMessageComponent={SessionMessageComponent} />;
  }

  const onFinancialInstitutionsClick = () => {
    window.location = 'https://ai.tornado.com';
  };
  const onIndividualInvestorsClick = () => {
    window.location = 'https://tornado.com/investing';
  };
  const onBrokerageSolutionsClick = () => {
    window.location = 'https://tornado.com/enterprise';
  };

  return (
    <Layout section="home" noHeader noFooter>
      <Wrapper>
        {SessionMessageComponent}

        <TornadoOutlinesWrapper Left={calcLeft(size.height, size.width)}>
          <SizeableTornadoLines isDarkMode={true} height={size.height} />
        </TornadoOutlinesWrapper>

        <GradientWrapper>
          <LeftGradientWrapper>
            <CircularGradient size={size.width} color={'#DCDBFF'} />
          </LeftGradientWrapper>
          <RightGradientWrapper>
            <CircularGradient size={size.width} color={'#9FFBF5'} />
          </RightGradientWrapper>
        </GradientWrapper>

        <FrameWrapper>
          <LogoWrapper>
            <FullLogo isDarkMode />
          </LogoWrapper>
          <JumboMessage>
            <h1>Investing, in&nbsp;the future</h1>
          </JumboMessage>
          <ActionsWrapper>
            <SkeletonButton onClick={onFinancialInstitutionsClick}>AI for Financial Institutions</SkeletonButton>
            <SkeletonButton onClick={onIndividualInvestorsClick}>Individual Investors</SkeletonButton>
            <SkeletonButton onClick={onBrokerageSolutionsClick}>Brokerage Solutions</SkeletonButton>
          </ActionsWrapper>
        </FrameWrapper>
      </Wrapper>
    </Layout>
  );
};

export default Home;
